'use client';

import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@routly/ui';
import { Plus, Trash2 } from 'lucide-react';
import { rowKey } from '@/lib/rowKey';
import type { Selector } from '@/lib/types';

const TYPES: Selector['type'][] = ['expression', 'geofence', 'datetime', 'activity'];

/** A fresh row of each type: shared objects would alias between rows. */
const blank: Record<Selector['type'], () => Selector> = {
  expression: () => ({ type: 'expression', expr: '' }),
  geofence: () => ({ type: 'geofence', geofences: [], inside: true }),
  datetime: () => ({ type: 'datetime', days: [] }),
  activity: () => ({ type: 'activity', calculator: '' }),
};

interface SelectorFieldsProps {
  selectors: Selector[];
  onChange: (selectors: Selector[]) => void;
}

/**
 * What makes the calculator active. Selectors AND together.
 *
 * That they combine with AND rather than OR is stated on screen because it is
 * the difference between "speeding inside the depot" and "speeding, or inside
 * the depot" — and the second one fires all day.
 */
export function SelectorFields({ selectors, onChange }: SelectorFieldsProps) {
  const replace = (index: number, selector: Selector) =>
    onChange(selectors.map((existing, i) => (i === index ? selector : existing)));

  return (
    <div className="space-y-3">
      {selectors.map((selector, index) => (
        <div key={rowKey(selector)} className="flex items-start gap-2">
          <Select
            value={selector.type}
            onValueChange={(value) => replace(index, blank[value as Selector['type']]())}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1">
            <Body selector={selector} onChange={(next) => replace(index, next)} />
          </div>

          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove selector ${index + 1}`}
            onClick={() => onChange(selectors.filter((_, i) => i !== index))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {selectors.length > 1 ? (
        <p className="text-xs text-muted-foreground">
          All of these must hold at once. Any one of them false makes the calculator inactive.
        </p>
      ) : null}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...selectors, blank.expression()])}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add condition
      </Button>
    </div>
  );
}

function Body({
  selector,
  onChange,
}: {
  selector: Selector;
  onChange: (selector: Selector) => void;
}) {
  if (selector.type === 'expression') {
    return (
      <Input
        aria-label="Expression"
        className="font-mono"
        placeholder="$ignition && $speed > 3"
        value={selector.expr}
        onChange={(event) => onChange({ ...selector, expr: event.target.value })}
      />
    );
  }

  if (selector.type === 'activity') {
    return (
      <Input
        aria-label="Calculator slug"
        placeholder="trip"
        value={selector.calculator}
        onChange={(event) => onChange({ ...selector, calculator: event.target.value })}
      />
    );
  }

  if (selector.type === 'geofence') {
    return (
      <Input
        aria-label="Geofence ids"
        placeholder="leave empty for whichever geofence this partition is about"
        value={selector.geofences.join(', ')}
        onChange={(event) =>
          onChange({
            ...selector,
            geofences: event.target.value
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean),
          })
        }
      />
    );
  }

  return (
    <div className="flex gap-2">
      <Input
        aria-label="From"
        placeholder="08:00"
        value={selector.from ?? ''}
        onChange={(event) => onChange({ ...selector, from: event.target.value || undefined })}
      />
      <Input
        aria-label="To"
        placeholder="18:00"
        value={selector.to ?? ''}
        onChange={(event) => onChange({ ...selector, to: event.target.value || undefined })}
      />
      <span className="self-center text-xs text-muted-foreground">UTC</span>
    </div>
  );
}
